// Local-storage helpers for the email-integration UX surfaces.
// Dismissal state lives client-side; the manual-forward address is a static
// config value (the backend's forward@jobbeast.org mailbox).

import { AutoApplyOfferSurface } from '../types';

const SEED_CARD_DISMISSED = 'jb.seedCardDismissed';
const REPLY_CAPTURE_SEEN = 'jb.replyCaptureSeen';
const REPLY_CAPTURE_RETIRED = 'jb.replyCaptureModalRetired';
const APP_BANNER_DISMISSED_AT = 'jb.applicationsBannerDismissedAt';
const APP_HINT_DISMISSED_PREFIX = 'jb.appHintDismissed:';
const VOLUME_REASSURANCE_DISMISSED = 'jb.volumeReassuranceDismissed';
// One key per surface: dismissing the offer on Opportunities must not silence
// it on Applications, so the user is asked at most twice, then never again.
// The unsuffixed key is the original Opportunities-only one — keeping it means
// existing dismissals survive this change.
const AUTO_APPLY_OFFER_DISMISSED: Record<AutoApplyOfferSurface, string> = {
  opportunities: 'jb.autoApplyOfferDismissed',
  applications: 'jb.autoApplyOfferDismissed.applications',
};
const REJECTION_ENCOURAGEMENT_SEEN = 'jb.rejectionEncouragementSeen';

const BANNER_DECAY_DAYS = 30;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Threshold for the compact re-prompt modal (3rd send without capture).
export const THRESHOLD_COMPACT_REPROMPT = 3;

export const getGmailAddonUrl = (): string =>
  process.env.REACT_APP_GMAIL_ADDON_URL ||
  'https://workspace.google.com/marketplace/app/job_beast/35754788047';

export const getForwardEmail = (): string =>
  process.env.REACT_APP_FORWARD_EMAIL || 'forward@jobbeast.org';

export const isSeedCardDismissed = (): boolean =>
  localStorage.getItem(SEED_CARD_DISMISSED) === 'true';

export const dismissSeedCard = (): void => {
  localStorage.setItem(SEED_CARD_DISMISSED, 'true');
};

export const hasSeenReplyCaptureModal = (): boolean =>
  localStorage.getItem(REPLY_CAPTURE_SEEN) === 'true';

export const markReplyCaptureModalSeen = (): void => {
  localStorage.setItem(REPLY_CAPTURE_SEEN, 'true');
};

export const isReplyCaptureModalRetired = (): boolean =>
  localStorage.getItem(REPLY_CAPTURE_RETIRED) === 'true';

export const retireReplyCaptureModal = (): void => {
  localStorage.setItem(REPLY_CAPTURE_RETIRED, 'true');
  localStorage.setItem(REPLY_CAPTURE_SEEN, 'true');
};

export const isApplicationsBannerActive = (): boolean => {
  const dismissedAt = localStorage.getItem(APP_BANNER_DISMISSED_AT);
  if (!dismissedAt) return true;
  const elapsed = Date.now() - parseInt(dismissedAt, 10);
  return elapsed > BANNER_DECAY_DAYS * ONE_DAY_MS;
};

export const dismissApplicationsBanner = (): void => {
  localStorage.setItem(APP_BANNER_DISMISSED_AT, String(Date.now()));
};

export const isAppHintDismissed = (applicationId: string): boolean =>
  localStorage.getItem(APP_HINT_DISMISSED_PREFIX + applicationId) === '1';

export const dismissAppHint = (applicationId: string): void => {
  localStorage.setItem(APP_HINT_DISMISSED_PREFIX + applicationId, '1');
};

// Apply-flow "keep applying — volume wins" reassurance: dismiss once, stays dismissed.
export const isVolumeReassuranceDismissed = (): boolean =>
  localStorage.getItem(VOLUME_REASSURANCE_DISMISSED) === 'true';

export const dismissVolumeReassurance = (): void => {
  localStorage.setItem(VOLUME_REASSURANCE_DISMISSED, 'true');
};

// Auto-Apply offer card: dismiss once per surface, stays dismissed (the card
// also self-hides for good once Auto-Apply is actually enabled).
export const isAutoApplyOfferDismissed = (surface: AutoApplyOfferSurface): boolean =>
  localStorage.getItem(AUTO_APPLY_OFFER_DISMISSED[surface]) === 'true';

export const dismissAutoApplyOffer = (surface: AutoApplyOfferSurface): void => {
  localStorage.setItem(AUTO_APPLY_OFFER_DISMISSED[surface], 'true');
};

// Rejection reassurance: show on the FIRST rejection only, then never again (no nag).
export const hasSeenRejectionEncouragement = (): boolean =>
  localStorage.getItem(REJECTION_ENCOURAGEMENT_SEEN) === 'true';

export const markRejectionEncouragementSeen = (): void => {
  localStorage.setItem(REJECTION_ENCOURAGEMENT_SEEN, 'true');
};
